import * as signalR from '@microsoft/signalr'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001'
const isDevelopment = process.env.NODE_ENV === 'development'

// Type definitions
export interface ChatMessage {
  id: number
  conversationId: number
  senderId: number
  content: string
  createdAt: string
  isRead: boolean
}

export interface Notification {
  id: number
  type: string
  title: string
  message: string
  isRead: boolean
  createdAt: string
  data?: Record<string, unknown>
}

let chatConnection: signalR.HubConnection | null = null
let notificationConnection: signalR.HubConnection | null = null

// Conditional logging helper
function log(message: string, data?: unknown): void {
  if (isDevelopment) {
    if (data !== undefined) {
      console.log(message, data)
    } else {
      console.log(message)
    }
  }
}

function logError(message: string, error?: unknown): void {
  if (isDevelopment) {
    console.error(message, error)
  }
}

// Helper to get access token from cookie for query string fallback
// NOTE: This is a fallback - we'll try cookie auth first via withCredentials
async function getAccessTokenFromCookie(): Promise<string> {
  try {
    // Try to get token from /api/auth/me endpoint which will send cookies
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include'
    })

    if (response.ok) {
      // If request succeeded, we're authenticated via cookie
      // Return empty string as SignalR will use cookies automatically
      return ''
    }
  } catch (error) {
    logError('Failed to verify authentication:', error)
  }

  return ''
}

// Chat Hub Connection
export function getChatConnection(): signalR.HubConnection | null {
  return chatConnection
}

export async function connectChat(): Promise<signalR.HubConnection> {
  if (chatConnection?.state === signalR.HubConnectionState.Connected) {
    return chatConnection
  }

  chatConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${API_URL}/hubs/chat`, {
      // Use HttpOnly cookies for authentication
      withCredentials: true,
      // Fallback to query string if cookies don't work (shouldn't be needed)
      accessTokenFactory: getAccessTokenFromCookie,
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build()

  chatConnection.onclose((error) => {
    log('Chat connection closed', error)
  })

  chatConnection.onreconnecting((error) => {
    log('Chat connection reconnecting', error)
  })

  chatConnection.onreconnected((connectionId) => {
    log('Chat connection reconnected', connectionId)
  })

  try {
    await chatConnection.start()
    log('Chat connection started')
  } catch (error) {
    logError('Error starting chat connection:', error)
    throw error
  }

  return chatConnection
}

export async function disconnectChat(): Promise<void> {
  if (chatConnection) {
    await chatConnection.stop()
    chatConnection = null
  }
}

// Notification Hub Connection
export function getNotificationConnection(): signalR.HubConnection | null {
  return notificationConnection
}

export async function connectNotifications(): Promise<signalR.HubConnection> {
  if (notificationConnection?.state === signalR.HubConnectionState.Connected) {
    return notificationConnection
  }

  notificationConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${API_URL}/hubs/notifications`, {
      // Use HttpOnly cookies for authentication
      withCredentials: true,
      // Fallback to query string if cookies don't work (shouldn't be needed)
      accessTokenFactory: getAccessTokenFromCookie,
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build()

  notificationConnection.onclose((error) => {
    log('Notification connection closed', error)
  })

  notificationConnection.onreconnecting((error) => {
    log('Notification connection reconnecting', error)
  })

  notificationConnection.onreconnected((connectionId) => {
    log('Notification connection reconnected', connectionId)
  })

  try {
    await notificationConnection.start()
    log('Notification connection started')
  } catch (error) {
    logError('Error starting notification connection:', error)
    throw error
  }

  return notificationConnection
}

export async function disconnectNotifications(): Promise<void> {
  if (notificationConnection) {
    await notificationConnection.stop()
    notificationConnection = null
  }
}

// Chat Hub Methods
export async function joinConversation(conversationId: number): Promise<void> {
  if (chatConnection?.state === signalR.HubConnectionState.Connected) {
    await chatConnection.invoke('JoinConversation', conversationId)
  }
}

export async function leaveConversation(conversationId: number): Promise<void> {
  if (chatConnection?.state === signalR.HubConnectionState.Connected) {
    await chatConnection.invoke('LeaveConversation', conversationId)
  }
}

export async function sendTyping(conversationId: number): Promise<void> {
  if (chatConnection?.state === signalR.HubConnectionState.Connected) {
    await chatConnection.invoke('SendTyping', conversationId)
  }
}

export async function stopTyping(conversationId: number): Promise<void> {
  if (chatConnection?.state === signalR.HubConnectionState.Connected) {
    await chatConnection.invoke('StopTyping', conversationId)
  }
}

export async function markAsRead(conversationId: number, messageId: number): Promise<void> {
  if (chatConnection?.state === signalR.HubConnectionState.Connected) {
    await chatConnection.invoke('MarkAsRead', conversationId, messageId)
  }
}

// Notification Hub Methods
export async function markNotificationRead(notificationId: number): Promise<void> {
  if (notificationConnection?.state === signalR.HubConnectionState.Connected) {
    await notificationConnection.invoke('MarkNotificationRead', notificationId)
  }
}

export async function markAllNotificationsRead(): Promise<void> {
  if (notificationConnection?.state === signalR.HubConnectionState.Connected) {
    await notificationConnection.invoke('MarkAllNotificationsRead')
  }
}

// Event Handlers - Chat
export function onNewMessage(callback: (message: ChatMessage) => void): void {
  chatConnection?.on('NewMessage', callback)
}

export function onUserTyping(callback: (data: { conversationId: number; userId: number; timestamp: string }) => void): void {
  chatConnection?.on('UserTyping', callback)
}

export function onUserStoppedTyping(callback: (data: { conversationId: number; userId: number }) => void): void {
  chatConnection?.on('UserStoppedTyping', callback)
}

export function onMessagesRead(callback: (data: { conversationId: number; userId: number; lastReadMessageId: number; timestamp: string }) => void): void {
  chatConnection?.on('MessagesRead', callback)
}

// Event Handlers - Notifications
export function onNewNotification(callback: (notification: Notification) => void): void {
  notificationConnection?.on('NewNotification', callback)
}

export function onNotificationRead(callback: (notificationId: number) => void): void {
  notificationConnection?.on('NotificationRead', callback)
}

export function onAllNotificationsRead(callback: () => void): void {
  notificationConnection?.on('AllNotificationsRead', callback)
}

// Remove event handlers
export function offNewMessage(callback?: (message: ChatMessage) => void): void {
  if (callback) {
    chatConnection?.off('NewMessage', callback)
  } else {
    chatConnection?.off('NewMessage')
  }
}

export function offNewNotification(callback?: (notification: Notification) => void): void {
  if (callback) {
    notificationConnection?.off('NewNotification', callback)
  } else {
    notificationConnection?.off('NewNotification')
  }
}
